import { Reset } from "assets/icons/Common";
import EmojiBlue from "assets/Misc/Wellness/Emojis_Blue.png";
import EmojiGreen from "assets/Misc/Wellness/Emojis_Green.png";
import EmojiRed from "assets/Misc/Wellness/Emojis_Red.png";
import EmojiYellow from "assets/Misc/Wellness/Emojis_Yellow.png";
import classNames from "classnames";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import React, { useEffect, useRef, useState } from "react";
import Draggable, { DraggableEvent } from "react-draggable";
import { DragCallbackData } from "react-resizable";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import useSound from "use-sound";
import { elementsOverlap } from "util/elementsOverlay";

type DroppedNames = { [key: string]: { [index: number]: string } };

export interface WellbeingCheckState {
  droppedNames: DroppedNames;
}

export const DropZoneConfig: { [key: string]: any } = {
  green: { color: "#7FDFDB", label: "Green", emoji: EmojiGreen },
  blue: { color: "#80A0B6", label: "Blue", emoji: EmojiBlue },
  yellow: { color: "#FEE9A5", label: "Yellow", emoji: EmojiYellow },
  red: { color: "#FF9090", label: "Red", emoji: EmojiRed },
};
const DropZoneKeys = Object.keys(DropZoneConfig);
const DefaultNamesState = () =>
  DropZoneKeys.reduce<DroppedNames>((dict, el) => ({ ...dict, [el]: {} }), {});

export const WellbeingCheck: WidgetFC<WellbeingCheckState> = ({ state, setState, settings }) => {
  const { droppedNames } = state || { droppedNames: DefaultNamesState() };
  const {
    names,
    emojis,
    sound,
    selectSoundGreen,
    selectSoundBlue,
    selectSoundYellow,
    selectSoundRed,
  } = settings;
  const dropZoneRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {
    green: useRef<HTMLDivElement>(null),
    blue: useRef<HTMLDivElement>(null),
    yellow: useRef<HTMLDivElement>(null),
    red: useRef<HTMLDivElement>(null),
  };
  const [currentHoverDropzone, setCurrentHoverDropzone] = useState<string | undefined>();
  const [currentDragIndex, setCurrentDragIndex] = useState<number | undefined>();
  const namesList = names
    ? (names as string).split("\n").filter((item) => Boolean(item.trim()))
    : [];

  const selectedGreenSound = require("assets/sounds/" + (selectSoundGreen || "yoo-hoo.mp3"));
  const selectedBlueSound = require("assets/sounds/" + (selectSoundBlue || "yoo-hoo.mp3"));
  const selectedYellowSound = require("assets/sounds/" + (selectSoundYellow || "yoo-hoo.mp3"));
  const selectedRedSound = require("assets/sounds/" + (selectSoundRed || "yoo-hoo.mp3"));
  const [playGreen] = useSound(selectedGreenSound);
  const [playBlue] = useSound(selectedBlueSound);
  const [playYellow] = useSound(selectedYellowSound);
  const [playRed] = useSound(selectedRedSound);

  useEffect(() => {
    if (!droppedNames || Object.keys(droppedNames).length === 0) {
      setState({ droppedNames: DefaultNamesState() });
    }
  }, []);

  const onDragStop = (e: DraggableEvent, data: DragCallbackData) => {
    console.log("ss");

    if (currentHoverDropzone !== undefined && currentDragIndex !== undefined && droppedNames) {
      const newDroppedNames = droppedNames ? { ...droppedNames } : {};
      newDroppedNames[currentHoverDropzone][currentDragIndex] = namesList[currentDragIndex];
      setState({ droppedNames: newDroppedNames });

      if (sound) {
        switch (currentHoverDropzone) {
          case "green":
            playGreen();
            break;
          case "blue":
            playBlue();
            break;
          case "yellow":
            playYellow();
            break;
          case "red":
            playRed();
            break;
        }
      }
    }

    setCurrentDragIndex(undefined);
    setCurrentHoverDropzone(undefined);
  };

  const onDrag = (e: DraggableEvent, data: DragCallbackData) => {
    let anyHover = false;
    for (let key of DropZoneKeys) {
      if (dropZoneRefs[key].current && elementsOverlap(data.node, dropZoneRefs[key].current!)) {
        setCurrentHoverDropzone(key);
        anyHover = true;
        break;
      }
    }

    if (!anyHover) {
      setCurrentHoverDropzone(undefined);
    }
  };

  const onDragStart = (e: DraggableEvent, data: DragCallbackData, index: number) => {
    setCurrentDragIndex(index);
  };

  const isDropped = (index: number) => {
    if (!droppedNames) {
      return false;
    }
    for (let key of DropZoneKeys) {
      if (droppedNames[key] && droppedNames[key][index] !== undefined) {
        return true;
      }
    }
    return false;
  };

  const getTotalDropped = () => {
    if (!droppedNames) {
      return 0;
    }
    let total = 0;
    for (let key of DropZoneKeys) {
      total += droppedNames[key] ? Object.keys(droppedNames[key])?.length : 0;
    }
    return total;
  };

  const resetPressed = () => {
    setState({ droppedNames: DefaultNamesState() });
  };

  const getNameFontSize = (name: string) => {
    let size = 2.5;
    if (name.length > 10) {
      size = lerp(2.5, 2, (name.length - 10) / (15 - 10));
    }

    return `${size}cqw`;
  };

  return (
    <OuterContainer>
      <Row style={{ alignItems: "flex-start" }}>
        <Header>
          <Row>
            <h2>Wellbeing Check-in</h2>
            <span>
              {getTotalDropped()}/{namesList?.length || 0}
            </span>
          </Row>
          <p style={{ fontSize: "2cqw" }}>
            Drag your name to the colour that matches how you feel today
          </p>
        </Header>
        <ResetButton
          accentColor="var(--danger)"
          Icon={Reset}
          withShadow={false}
          onClick={resetPressed}
        />
      </Row>
      {Boolean(namesList.length) && (
        <NamesContainer>
          {namesList.map((name, index) => (
            <DragNameContainer key={index} isDragging={currentDragIndex === index}>
              <InactiveDragName style={{ fontSize: getNameFontSize(name) }}>
                <p>{name}</p>
              </InactiveDragName>
              {!isDropped(index) && (
                <Draggable
                  position={{ x: 0, y: 0 }}
                  onStart={(e, d) => onDragStart(e, d, index)}
                  onDrag={onDrag}
                  onStop={onDragStop}
                >
                  <DragName style={{ fontSize: getNameFontSize(name) }}>
                    <p>{name}</p>
                  </DragName>
                </Draggable>
              )}
            </DragNameContainer>
          ))}
        </NamesContainer>
      )}
      <DropContainer>
        {DropZoneKeys.map((key) => (
          <DropZone
            key={key}
            ref={dropZoneRefs[key]}
            color={DropZoneConfig[key].color}
            isHover={currentHoverDropzone === key}
          >
            {emojis && <DropZoneImg src={DropZoneConfig[key].emoji} />}
          </DropZone>
        ))}
      </DropContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  height: 100%;
  padding: 5cqh 5.25cqw;
  justify-content: flex-start;
  gap: 2.5cqh 0;
`;

const Header = styled(Col)`
  margin-bottom: 3cqh;

  h2 {
    font-weight: 600;
    margin-bottom: 0px;
    font-size: 3.5cqw;
    margin-right: 2cqw;
  }
  span {
    color: var(--widget-primary);
    font-size: 3.5cqw;
    line-height: 1.2em;
  }
  p {
    margin-top: 1cqh;
    color: var(--secondary);
  }
`;

const ResetButton = styled(IconButtonInverted)`
  width: 6cqw;
  height: 6cqw;
  margin: auto 0px auto auto;
  padding: 1.3cqw;
`;

const NamesContainer = styled.div.attrs({ className: classNames("no-drag") })`
  gap: 2cqw 1.3cqh;
  display: grid;
  grid-template-columns: repeat(5, minmax(15cqw, 1fr));
  height: 45cqh;
  grid-auto-rows: 5.5cqh;
`;

const DragNameContainer = styled.div<{ isDragging: boolean }>`
  position: relative;
  font-size: 2.5cqw;
  font-weight: 600;
  width: 17cqw;
  z-index: ${(props) => (props.isDragging ? 5 : 1)};
  line-height: 1em;
  word-break: break-word;

  p {
    text-align: center;
  }
`;

const InactiveDragName = styled(Row)`
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: #efefef;
  aspect-ratio: 2.5;
  justify-content: center;
  align-items: center;
  border-radius: 5px;

  p {
    color: #b4b4b4;
  }
`;

const DragName = styled(Row)`
  box-shadow: 0px 0px 3px 0px;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background-color: white;
  z-index: 1;

  p {
    color: var(--widget-primary);
  }

  &:hover {
    filter: brightness(95%);
  }
`;

const DropContainer = styled(Row)`
  width: 100%;
  flex-grow: 1;
  max-height: 20%;
  justify-content: space-between;
  margin-top: auto;
`;

const DropZone = styled(Col).attrs<{ color: string; isHover: boolean }>(({ isHover }) => ({
  className: classNames(isHover && "isHover"),
}))<{ color: string; isHover: boolean }>`
  height: 100%;
  width: 20%;
  background-color: ${(props) => props.color};
  border-radius: 2px 2px 5px 5px;
  box-shadow: 0px -8px 4cqh 0px rgba(0, 0, 0, 0.2);
  justify-content: center;
  align-items: center;

  &.isHover {
    outline: 3px dashed var(--primary);
  }
`;

const DropZoneImg = styled.img`
  height: 100%;
  object-fit: cover;
`;
